import { createContext, useContext, useState, useCallback } from 'react';

const translations = {
  en: {
    // Landing
    "nav.product": "Product",
    "nav.pricing": "Pricing",
    "nav.login": "Log In",
    "nav.start": "Free Audit",
    "hero.tag": "FINANCIAL MODEL AUDIT",
    "hero.title": "The insurance policy your spreadsheets never had.",
    "hero.subtitle": "Excel errors have cost companies billions. SheetArmor uses deterministic analysis + AI engine to find risks before they find you.",
    "hero.cta": "START FREE AUDIT",
    "hero.cta2": "SEE HOW IT WORKS",
    "hero.stat1.value": "$6.2B",
    "hero.stat1.label": "JPMorgan London Whale loss — partly from a spreadsheet error",
    "hero.stat2.value": "88%",
    "hero.stat2.label": "of spreadsheets contain at least one error",
    "hero.stat3.value": "<5min",
    "hero.stat3.label": "Average audit time with SheetArmor",
    // Features
    "features.tag": "DETECTION ENGINE",
    "features.title": "What we detect",
    "features.subtitle": "Hybrid analysis: deterministic Python code for precision, AI engine for interpretation.",
    "feat.hardcoded.title": "Hardcoded Values",
    "feat.hardcoded.desc": "Detects manually typed numbers hidden within formula columns — the #1 source of model errors.",
    "feat.formula.title": "Formula Inconsistencies",
    "feat.formula.desc": "Identifies formulas that deviate from column patterns — copy-paste errors caught instantly.",
    "feat.circular.title": "Circular References",
    "feat.circular.desc": "Maps cell dependencies to detect circular logic that can cascade into catastrophic errors.",
    "feat.external.title": "Broken External Links",
    "feat.external.desc": "Flags references to external files that may be missing, moved, or outdated.",
    "feat.hidden.title": "Hidden Cells & Sheets",
    "feat.hidden.desc": "Reveals concealed rows, columns, and sheets that could mask critical data or errors.",
    "feat.ai.title": "AI Risk Report",
    "feat.ai.desc": "Our AI engine transforms technical findings into a professional PDF report with actionable recommendations.",
    // How it works
    "how.tag": "HOW IT WORKS",
    "how.title": "Three steps to audit-grade confidence",
    "how.step1.title": "Upload",
    "how.step1.desc": "Drag and drop your .xlsx file. No macros are executed — ever.",
    "how.step2.title": "Analyze",
    "how.step2.desc": "Our engine scans every cell, formula, and reference in seconds.",
    "how.step3.title": "Download",
    "how.step3.desc": "Download a professional PDF report ready for board-level presentation.",
    // Pricing
    "pricing.tag": "PRICING",
    "pricing.title": "Simple, transparent pricing",
    "pricing.monthly": "Monthly",
    "pricing.annual": "Annual",
    "pricing.save_17": "Save 17%",
    "pricing.months_free": "2 months free",
    "pricing.billed_yearly": "Billed {price} yearly",
    "pricing.plan1": "Auditor",
    "pricing.plan1.price.usd": "$99",
    "pricing.plan1.price.eur": "99€",
    "pricing.plan1.period": "/month",
    "pricing.plan1.desc": "For individual analysts & accountants",
    "pricing.plan1.feat1": "10 audits per month",
    "pricing.plan1.feat2": "AI-powered PDF reports",
    "pricing.plan1.feat3": "24h auto data deletion (GDPR/SOC2)",
    "pricing.plan1.feat4": "Complete audit + detail of first 50 errors",
    "pricing.plan1.feat5": "Bilingual reports (EN/FR)",
    "pricing.plan1.cta": "Secure my models",
    "pricing.plan2": "Firm",
    "pricing.plan2.price.usd": "$249",
    "pricing.plan2.price.eur": "249€",
    "pricing.plan2.period": "/month",
    "pricing.plan2.desc": "For M&A teams & advisory firms",
    "pricing.plan2.feat1": "Unlimited audits",
    "pricing.plan2.feat2": "AI-powered PDF reports",
    "pricing.plan2.feat3": "24h auto data deletion (GDPR/SOC2)",
    "pricing.plan2.feat4": "Complete audit + Exhaustive detail of all errors (Unlimited CSV export)",
    "pricing.plan2.feat5": "Manual instant data destruction button (Kill-Switch)",
    "pricing.plan2.feat6": "Bilingual reports (EN/FR)",
    "pricing.plan2.cta": "Go Enterprise",
    "pricing.no_commitment": "No commitment — Cancel anytime",
    "pricing.corporate": "Corporate email required",
    "pricing.legal_notice": "Service available exclusively for businesses in France 🇫🇷 and the US 🇺🇸. VAT not applicable.",
    // CTA
    "cta.title": "Stop trusting your spreadsheets blindly.",
    "cta.subtitle": "The next billion-dollar error is hiding in a cell right now.",
    "cta.button": "GET STARTED FREE",
    // FAQ Section
    "faq.tag": "FREQUENTLY ASKED QUESTIONS",
    "faq.title": "Common Questions",
    "faq.q1": "How does SheetArmor protect my confidential data?",
    "faq.a1": "We guarantee 100% confidentiality. Your Excel files are automatically deleted after 24 hours. Our AI partner (Anthropic) contractually commits to never train their models on your data. Your files never leave European servers (AWS eu-west-1).",
    "faq.q2": "What file formats and sizes are supported?",
    "faq.a2": "SheetArmor only accepts .xlsx files (Excel 2007+), up to 50MB. Password-protected or encrypted files are automatically rejected for security reasons. Macros are never executed.",
    "faq.q3": "What if the tool reports false positives?",
    "faq.a3": "SheetArmor detects logical anomalies and pattern breaks. Some alerts may be intentional in your model. You, as the expert, decide which findings require action. The tool provides diagnostic information, not definitive judgments.",
    "faq.q4": "What's the difference between Auditor and Firm plans?",
    "faq.a4": "Auditor plan is limited in number of audits per month, while the Firm plan offers unlimited audits. Auditor plan provides PDF reports limited to the first 50 errors (ideal for quick diagnostics). Firm plan includes unlimited CSV export with exhaustive details of all errors (2000+), plus the Kill-Switch feature for instant data destruction.",
    "faq.q5": "My file fails to analyze, what should I do?",
    "faq.a5": "First, verify that your file is not password-protected. In case of persistent bugs, contact hello@sheetarmor.io. To diagnose the issue, our engineering team may ask you to voluntarily share the file via a secure channel under a confidentiality agreement (NDA). This is strictly at your discretion.",
    // Security Section
    "security.tag": "ENTERPRISE SECURITY",
    "security.title": "Designed for CFOs",
    "security.subtitle": "We understand your financial data is strategic. Our architecture ensures absolute confidentiality and total compliance.",
    "security.zero_retention.title": "Zero-Retention Architecture",
    "security.zero_retention.desc": "Your Excel files are physically destroyed after 24h. No copies, no backups, no archives. Automatic unconditional deletion.",
    "security.zero_training.title": "Zero-Training Guarantee",
    "security.zero_training.desc": "Our LLM partners (Anthropic) contractually commit to never training their models on your financial data. B2B guarantee.",
    "security.sovereignty.title": "European Hosting",
    "security.sovereignty.desc": "100% European infrastructure (AWS eu-west-1). Native GDPR compliance. Your data never leaves the European Union.",
    "security.read_only.title": "Read-Only Analysis",
    "security.read_only.desc": "Your original file integrity is guaranteed. No macros executed. No modifications possible. Read-only analysis.",
    "security.encryption.title": "Bank-Grade Encryption",
    "security.encryption.desc": "TLS 1.3 end-to-end. Stripe payments (PCI-DSS Level 1). HTTP-only session cookies. Banking security for your financial models.",
    "security.fast.title": "Fast & Efficient",
    "security.fast.desc": "Complete audit in less than 5 minutes. No installation required. Compatible with all your .xlsx files up to 50MB.",
    "security.badge": "SOC2 compliant architecture — Zero Data Retention — Models are not trained on your data.",
    // Teaser/Paywall
    "teaser.locked": "Full report locked",
    "teaser.unlock": "UNLOCK FULL AUDIT REPORT & CELL-BY-CELL DETAILS",
    "teaser.subscribe": "Subscribe now to access the complete analysis",
    "teaser.issues_found": "issues found",
    "teaser.affected_sheets": "Errors detected in sheets",
    // Rate limit
    "rate.limit": "Trial limit reached for your organization. Upgrade to continue auditing.",
    // B2B
    "b2b.error": "For security and compliance reasons, SheetArmor requires a valid corporate email address.",
    // Contact
    "contact.tag": "B2B CONTACT",
    "contact.title": "Contact Us",
    "contact.subtitle": "Enterprise inquiries, security questions, or partnership opportunities. Our team responds within 24 hours.",
    "contact.name": "Full Name",
    "contact.email": "Professional Email",
    "contact.email_hint": "Corporate email required (no Gmail, Yahoo, etc.)",
    "contact.company": "Company",
    "contact.message": "Message",
    "contact.submit": "SEND MESSAGE",
    "contact.success_title": "Message received",
    "contact.success_message": "Your message has been forwarded to our security team. We will respond within 24 hours.",
    // Footer
    "footer.tagline": "The digital fortress for financial models.",
    "footer.copy": "SheetArmor. All rights reserved.",
    "footer.legal": "Legal Notice",
    "footer.terms": "Terms",
    "footer.privacy": "Privacy",
    "footer.contact": "Contact Us",
    "cookie.message": "We strictly use essential cookies to ensure your security and the site's functionality (zero ad tracking).",
    "cookie.button": "Got it",
    // Auth
    "auth.login": "Log In",
    "auth.register": "Create Account",
    "auth.email": "Email",
    "auth.password": "Password",
    "auth.name": "Full Name",
    "auth.first_name": "First Name",
    "auth.last_name": "Last Name",
    "auth.google": "CONTINUE WITH GOOGLE",
    "auth.or": "or",
    "auth.no_account": "Don't have an account?",
    "auth.has_account": "Already have an account?",
    "auth.left_panel_title": "Protect your financial models.",
    "auth.left_panel_desc": "Join finance professionals who trust SheetArmor to audit their Excel files before critical decisions.",
    "auth.generate_password": "GENERATE",
    "auth.generate_toast": "Password copied. Save it in a secure password manager (e.g. 1Password).",
    "auth.confirm_password": "Confirm Password",
    "auth.confirm_placeholder": "Repeat your password",
    "auth.pwd_mismatch": "Passwords do not match.",
    "auth.register_success": "Please confirm your account from your inbox. This may take a few minutes (remember to check your spam folder).",
    "auth.pwd_weak_hint": "Required: 12+ characters, 1 uppercase, 1 lowercase, 1 number, 1 symbol",
    "auth.pwd_strength_weak": "Weak",
    "auth.pwd_strength_medium": "Medium",
    "auth.pwd_strength_strong": "Bank-Grade ✓",
    "auth.email_exists": "An account already exists with this email address. Please log in or reset your password.",
    "auth.error_generic": "An unexpected error occurred. Please try again.",
    "auth.verify_required": "Please verify your email before logging in. Check your inbox (and spam folder).",
    "auth.resend_verification": "Resend verification email",
    "auth.resend_loading": "Sending...",
    "auth.resend_success": "Verification email sent! Check your inbox and spam folder.",
    "auth.zero_knowledge": "End-to-end encryption. Zero-Knowledge architecture.",
    "auth.register_subtitle": "Create your secure access with your corporate email",
    "auth.connecting": "Connecting...",
    "auth.pwd_strong_required": "Password must contain at least 12 characters, 1 uppercase, 1 lowercase, 1 digit and 1 symbol.",
    "auth.pwd_mismatch_error": "Passwords do not match.",
    // Dashboard
    "dash.title": "Audit Dashboard",
    "dash.upload": "NEW AUDIT",
    "dash.history": "Audit History",
    "dash.no_audits": "No audits yet. Upload your first Excel file to get started.",
    "dash.score": "Health Score",
    "dash.issues": "Issues",
    "dash.critical": "Critical",
    "dash.date": "Date",
    "dash.actions": "Actions",
    "dash.plan": "Current Plan",
    "dash.free": "Free",
    "dash.auditor": "Auditor",
    "dash.firm": "Firm",
    "dash.upgrade": "UPGRADE",
    "dash.expires": "Data expires in",
    "dash.hours": "hours",
    "dash.view": "View",
    "dash.download": "PDF",
    "dash.delete": "Delete",
    "dash.stats": "Overview",
    "dash.total_audits": "Total Audits",
    "dash.avg_score": "Avg. Score",
    "dash.total_issues": "Total Issues Found",
    "dash.usage": "Usage",
    "dash.audits_used": "Audits used: {count} / 10",
    "dash.audits_unlimited": "Audits: Unlimited",
    "dash.credits_reset": "Credits reset on {date}",
    "dash.manage_billing": "Manage my subscription",
    // Upload
    "upload.title": "Upload File",
    "upload.drag": "Drag & drop your .xlsx file here",
    "upload.or": "or click to browse",
    "upload.hint": "Only .xlsx files accepted — Max 50MB",
    "upload.analyzing": "Analyzing your spreadsheet...",
    "upload.step1": "Uploading file",
    "upload.step2": "Running deterministic analysis",
    "upload.step3": "Generating AI report",
    "upload.step4": "Building PDF",
    "upload.cta": "START AUDIT",
    "upload.drop_reject": "Only .xlsx files under 50MB are accepted.",
    "upload.failed": "Upload failed. Please try again.",
    // Report
    "report.title": "Audit Report",
    "report.score": "Health Score",
    "report.risk": "Risk Level",
    "report.summary": "Executive Summary",
    "report.alerts": "Critical Alerts",
    "report.findings": "Detailed Findings",
    "report.recommendations": "Recommendations",
    "report.download_pdf": "DOWNLOAD PDF",
    "report.download_csv": "CSV",
    "report.unlock_csv": "Unlock Full CSV (Firm Plan)",
    "report.csv_limit_notice": "Auditor plan is limited to first 50 errors. Upgrade to Firm plan to unlock exhaustive CSV export.",
    "report.kill_switch": "Destroy Data (Kill-Switch)",
    "report.kill_switch_locked": "Kill-Switch (Firm Plan)",
    "report.kill_switch_confirm": "Are you sure? This action is irreversible. All audit data will be permanently destroyed.",
    "report.back": "Back to Dashboard",
    "report.high": "HIGH",
    "report.medium": "MEDIUM",
    "report.low": "LOW",
    "report.file": "File",
    "report.issues": "Issues",
    "report.sheet": "Sheet",
    "report.cell": "Cell",
    "report.auditor_upsell_prefix": "Only the first 50 errors are displayed. ",
    "report.auditor_upsell_link": "Upgrade to Firm plan",
    "report.auditor_upsell_suffix": " to unlock the detail of all {count} errors.",
    "report.not_found": "Report not found.",
    "report.sheets": "sheets",
    "report.cells": "cells",
    "report.critical_count": "critical",
    "report.warnings_count": "warnings",
    "report.rec": "Rec",
    "report.free_limit_notice": "The Free plan is limited to the first 5 errors. Upgrade to a paid plan to unlock all cell details.",
    "report.auditor_limit_notice": "The Auditor plan is limited to the first 50 errors. Upgrade to Firm plan to unlock full CSV export.",
    "report.issue_type_hardcoded_value": "Hardcoded Value",
    "report.issue_type_formula_inconsistency": "Formula Inconsistency",
    "report.issue_type_circular_reference": "Circular Reference",
    "report.issue_type_external_reference": "External Reference",
    "report.issue_type_hidden_sheet": "Hidden Sheet",
    "report.issue_type_hidden_row": "Hidden Row",
    "report.issue_type_hidden_column": "Hidden Column",
    "report.issue_type_protected_sheet": "Protected Sheet",
    "report.priority_high": "HIGH",
    "report.priority_medium": "MEDIUM",
    "report.priority_low": "LOW",
    "report.severity_critical": "CRITICAL",
    "report.severity_warning": "WARNING",
    "report.severity_info": "INFO",
    "teaser.more_errors": "{count} more error(s) hidden. Upgrade to Auditor plan to unlock the full report.",
    "teaser.ghost_1": "Hardcoded constant in financial formula — manual override detected",
    "teaser.ghost_2": "Formula inconsistency across row range — broken reference chain",
    "teaser.ghost_3": "Circular dependency in calculation chain — recalculation loop",
    "teaser.ghost_4": "Magic number embedded in dynamic model — no named range defined",
    "teaser.ghost_5": "Inconsistent formula pattern detected in column range",
    "teaser.ghost_6": "External workbook link — dependency risk if source file moves",
    "teaser.ghost_7": "Hidden row contains active formula — audit visibility gap",
    "teaser.ghost_8": "Array formula mismatch — cell range out of sync with data",
    "teaser.ghost_9": "Self-referencing loop causing silent recalculation error",
    "rate.limit_prefix": "Trial limit reached (2 audits max).",
    "rate.limit_link": "Upgrade",
    "rate.limit_suffix": "to continue auditing.",
    // Settings
    "settings.title": "Settings",
    "settings.account": "Account",
    "settings.language": "Language",
    "settings.subscription": "Subscription",
    "settings.logout": "LOG OUT",
    "settings.en": "English",
    "settings.fr": "French",
    "settings.name": "Name",
    "settings.email": "Email",
    "settings.audits_used": "Audits Used",
    "settings.next_billing": "Next billing",
  },
  fr: {
    "nav.product": "Produit",
    "nav.pricing": "Tarifs",
    "nav.login": "Connexion",
    "nav.start": "Auditer gratuitement",
    "hero.tag": "AUDIT DE MODÈLES FINANCIERS",
    "hero.title": "L'assurance que vos tableurs n'ont jamais eue.",
    "hero.subtitle": "Les erreurs Excel ont coûté des milliards. SheetArmor utilise une analyse déterministe + IA pour trouver les risques avant qu'ils ne vous trouvent.",
    "hero.cta": "LANCER UN AUDIT GRATUIT",
    "hero.cta2": "COMMENT ÇA MARCHE",
    "hero.stat1.value": "6,2 Mds$",
    "hero.stat1.label": "Perte JPMorgan London Whale — en partie due à une erreur de tableur",
    "hero.stat2.value": "88%",
    "hero.stat2.label": "des tableurs contiennent au moins une erreur",
    "hero.stat3.value": "<5min",
    "hero.stat3.label": "Temps d'audit moyen avec SheetArmor",
    "features.tag": "MOTEUR DE DÉTECTION",
    "features.title": "Ce que nous détectons",
    "features.subtitle": "Analyse hybride : code Python déterministe pour la précision, moteur IA pour l'interprétation.",
    "feat.hardcoded.title": "Valeurs en Dur",
    "feat.hardcoded.desc": "Détecte les nombres saisis manuellement au milieu de colonnes de formules — la source #1 d'erreurs.",
    "feat.formula.title": "Incohérences de Formules",
    "feat.formula.desc": "Identifie les formules qui diffèrent du pattern de la colonne — erreurs de copier-coller détectées.",
    "feat.circular.title": "Références Circulaires",
    "feat.circular.desc": "Cartographie les dépendances pour détecter la logique circulaire pouvant cascader en erreurs.",
    "feat.external.title": "Liens Externes Rompus",
    "feat.external.desc": "Signale les références à des fichiers externes manquants, déplacés ou obsolètes.",
    "feat.hidden.title": "Cellules & Feuilles Cachées",
    "feat.hidden.desc": "Révèle les lignes, colonnes et feuilles cachées pouvant masquer des données critiques.",
    "feat.ai.title": "Rapport IA de Risque",
    "feat.ai.desc": "Notre moteur IA transforme les résultats techniques en rapport PDF professionnel avec recommandations.",
    "how.tag": "FONCTIONNEMENT",
    "how.title": "Trois étapes vers la confiance",
    "how.step1.title": "Déposer",
    "how.step1.desc": "Glissez-déposez votre fichier .xlsx. Aucune macro n'est exécutée.",
    "how.step2.title": "Analyser",
    "how.step2.desc": "Notre moteur scanne chaque cellule, formule et référence en secondes.",
    "how.step3.title": "Télécharger",
    "how.step3.desc": "Téléchargez un rapport PDF professionnel prêt pour la direction.",
    "pricing.tag": "TARIFS",
    "pricing.title": "Tarification simple et transparente",
    "pricing.monthly": "Mensuel",
    "pricing.annual": "Annuel",
    "pricing.save_17": "Économisez 17%",
    "pricing.months_free": "2 mois offerts",
    "pricing.billed_yearly": "Facturé {price} par an",
    "pricing.plan1": "Auditeur",
    "pricing.plan1.price.usd": "99$",
    "pricing.plan1.price.eur": "99€",
    "pricing.plan1.period": "/mois",
    "pricing.plan1.desc": "Pour analystes et experts-comptables",
    "pricing.plan1.feat1": "10 audits par mois",
    "pricing.plan1.feat2": "Rapports PDF IA",
    "pricing.plan1.feat3": "Suppression auto 24h (RGPD/SOC2)",
    "pricing.plan1.feat4": "Audit complet + détail des 50 premières erreurs",
    "pricing.plan1.feat5": "Rapports bilingues (EN/FR)",
    "pricing.plan1.cta": "Sécuriser mes modèles",
    "pricing.plan2": "Cabinet",
    "pricing.plan2.price.usd": "249$",
    "pricing.plan2.price.eur": "249€",
    "pricing.plan2.period": "/mois",
    "pricing.plan2.desc": "Pour équipes M&A et cabinets de conseil",
    "pricing.plan2.feat1": "Audits illimités",
    "pricing.plan2.feat2": "Rapports PDF IA",
    "pricing.plan2.feat3": "Suppression auto 24h (RGPD/SOC2)",
    "pricing.plan2.feat4": "Audit complet + Détail exhaustif de toutes les erreurs (Export CSV illimité)",
    "pricing.plan2.feat5": "Bouton manuel de destruction instantanée des données (Kill-Switch)",
    "pricing.plan2.feat6": "Rapports bilingues (EN/FR)",
    "pricing.plan2.cta": "Passer au Standard Enterprise",
    "pricing.no_commitment": "Sans engagement — Annulation en 1 clic",
    "pricing.corporate": "Email professionnel requis",
    "pricing.legal_notice": "Service réservé aux entreprises basées en France 🇫🇷 et aux États-Unis 🇺🇸. TVA non applicable (art. 293 B du CGI).",
    "cta.title": "Arrêtez de faire confiance aveuglément à vos tableurs.",
    "cta.subtitle": "La prochaine erreur à un milliard se cache dans une cellule en ce moment.",
    "cta.button": "COMMENCER GRATUITEMENT",
    // FAQ Section
    "faq.tag": "QUESTIONS FRÉQUENTES",
    "faq.title": "Questions Courantes",
    "faq.q1": "Comment SheetArmor protège-t-il mes données confidentielles ?",
    "faq.a1": "Nous garantissons une confidentialité à 100%. Vos fichiers Excel sont automatiquement supprimés après 24h. Notre partenaire IA (Anthropic) s'engage contractuellement à ne jamais entraîner leurs modèles sur vos données. Vos fichiers ne quittent jamais les serveurs européens (AWS eu-west-1).",
    "faq.q2": "Quels formats et tailles de fichiers sont supportés ?",
    "faq.a2": "SheetArmor n'accepte que les fichiers .xlsx (Excel 2007+), jusqu'à 50 Mo. Les fichiers protégés par mot de passe ou chiffrés sont automatiquement rejetés pour des raisons de sécurité. Les macros ne sont jamais exécutées.",
    "faq.q3": "Et si l'outil signale des faux positifs ?",
    "faq.a3": "SheetArmor détecte des anomalies logiques et des ruptures de patterns. Certaines alertes peuvent être intentionnelles dans votre modèle. Vous, en tant qu'expert, décidez quelles conclusions nécessitent une action. L'outil fournit des informations diagnostiques, pas des jugements définitifs.",
    "faq.q4": "Quelle est la différence entre les plans Auditeur et Cabinet ?",
    "faq.a4": "Le plan Auditeur est limité en nombre d'audits par mois, tandis que le plan Cabinet offre des audits illimités. Le plan Auditeur fournit des rapports PDF limités aux 50 premières erreurs (idéal pour un diagnostic rapide). Le plan Cabinet inclut l'export CSV illimité avec le détail exhaustif de toutes les erreurs (2000+), plus la fonctionnalité Kill-Switch pour destruction instantanée des données.",
    "faq.q5": "Mon fichier échoue à l'analyse, que faire ?",
    "faq.a5": "Vérifiez d'abord que votre fichier n'est pas protégé par mot de passe. En cas de bug persistant, contactez hello@sheetarmor.io. Pour diagnostiquer le problème, notre équipe d'ingénierie pourrait vous demander de nous transmettre volontairement le fichier via un canal sécurisé sous accord de confidentialité (NDA). Ceci est strictement à votre discrétion.",
    // Security Section
    "security.tag": "SÉCURITÉ ENTERPRISE",
    "security.title": "Conçu pour les Directeurs Financiers",
    "security.subtitle": "Nous comprenons que vos données financières sont stratégiques. Notre architecture est conçue pour garantir confidentialité absolue et conformité totale.",
    "security.zero_retention.title": "Zero-Retention Architecture",
    "security.zero_retention.desc": "Vos fichiers Excel sont détruits physiquement après 24h. Aucune copie, aucun backup, aucune archive. Suppression automatique inconditionnelle.",
    "security.zero_training.title": "Zero-Training Guarantee",
    "security.zero_training.desc": "Nos partenaires LLM (Anthropic) s'engagent contractuellement à ne jamais entraîner leurs modèles sur vos données financières. Garantie B2B.",
    "security.sovereignty.title": "Hébergement Souverain",
    "security.sovereignty.desc": "Infrastructure 100% européenne (AWS eu-west-1). Conformité RGPD native. Vos données ne quittent jamais l'Union Européenne.",
    "security.read_only.title": "Analyse Read-Only",
    "security.read_only.desc": "L'intégrité de votre fichier d'origine est garantie. Aucune macro exécutée. Aucune modification possible. Analyse en lecture seule.",
    "security.encryption.title": "Chiffrement Bank-Grade",
    "security.encryption.desc": "TLS 1.3 end-to-end. Paiements Stripe (PCI-DSS Level 1). Session HTTP-only cookies. Sécurité bancaire pour vos modèles financiers.",
    "security.fast.title": "Rapide & Efficace",
    "security.fast.desc": "Audit complet en moins de 5 minutes. Aucune installation requise. Compatible avec tous vos fichiers .xlsx jusqu'à 50 Mo.",
    "security.badge": "Architecture RGPD — Suppression instantanée après analyse — Aucune donnée utilisée pour l'entraînement IA.",
    "teaser.locked": "Rapport complet verrouillé",
    "teaser.unlock": "DÉBLOQUER LE RAPPORT COMPLET & DÉTAILS CELLULE PAR CELLULE",
    "teaser.subscribe": "Abonnez-vous pour accéder à l'analyse complète",
    "teaser.issues_found": "problèmes détectés",
    "teaser.affected_sheets": "Erreurs détectées dans les onglets",
    "rate.limit": "Limite d'essai atteinte pour votre organisation. Abonnez-vous pour continuer.",
    "b2b.error": "Pour des raisons de sécurité et conformité, SheetArmor requiert une adresse email professionnelle.",
    // Contact
    "contact.tag": "CONTACT B2B",
    "contact.title": "Contactez-nous",
    "contact.subtitle": "Demandes entreprise, questions de sécurité ou opportunités de partenariat. Notre équipe répond sous 24h.",
    "contact.name": "Nom complet",
    "contact.email": "E-mail professionnel",
    "contact.email_hint": "Email professionnel requis (pas de Gmail, Yahoo, etc.)",
    "contact.company": "Société",
    "contact.message": "Message",
    "contact.submit": "ENVOYER",
    "contact.success_title": "Message transmis",
    "contact.success_message": "Votre message a été transmis à notre équipe de sécurité. Nous vous répondons sous 24h.",
    // Footer
    "footer.tagline": "La forteresse numérique pour les modèles financiers.",
    "footer.copy": "SheetArmor. Tous droits réservés.",
    "footer.legal": "Mentions Légales",
    "footer.terms": "CGU",
    "footer.privacy": "Confidentialité",
    "footer.contact": "Nous contacter",
    "cookie.message": "Nous utilisons exclusivement des cookies strictement nécessaires pour assurer votre sécurité et le fonctionnement du site (zéro tracking publicitaire).",
    "cookie.button": "Compris",
    "auth.login": "Connexion",
    "auth.register": "Créer un Compte",
    "auth.email": "Email",
    "auth.password": "Mot de passe",
    "auth.name": "Nom complet",
    "auth.first_name": "Prénom",
    "auth.last_name": "Nom",
    "auth.google": "CONTINUER AVEC GOOGLE",
    "auth.or": "ou",
    "auth.no_account": "Pas encore de compte ?",
    "auth.has_account": "Déjà un compte ?",
    "auth.left_panel_title": "Protégez vos modèles financiers.",
    "auth.left_panel_desc": "Rejoignez les professionnels de la finance qui font confiance à SheetArmor pour auditer leurs fichiers Excel avant les décisions critiques.",
    "auth.generate_password": "GÉNÉRER",
    "auth.generate_toast": "Mot de passe copié. Sauvegardez-le dans un gestionnaire de mots de passe sécurisé (ex: 1Password).",
    "auth.confirm_password": "Confirmer le mot de passe",
    "auth.confirm_placeholder": "Répétez votre mot de passe",
    "auth.pwd_mismatch": "Les mots de passe ne correspondent pas.",
    "auth.register_success": "Veuillez confirmer votre compte depuis votre boîte mail. Cela peut prendre quelques minutes (pensez à vérifier vos courriers indésirables).",
    "auth.pwd_weak_hint": "Requis : 12 caractères minimum, 1 majuscule, 1 minuscule, 1 chiffre, 1 symbole",
    "auth.pwd_strength_weak": "Faible",
    "auth.pwd_strength_medium": "Moyen",
    "auth.pwd_strength_strong": "Bank-Grade ✓",
    "auth.email_exists": "Un compte existe déjà avec cette adresse e-mail. Veuillez vous connecter ou réinitialiser votre mot de passe.",
    "auth.error_generic": "Une erreur inattendue est survenue. Veuillez réessayer.",
    "auth.verify_required": "Veuillez vérifier votre e-mail avant de vous connecter. Consultez votre boîte mail (et vos spams).",
    "auth.resend_verification": "Renvoyer l'e-mail de vérification",
    "auth.resend_loading": "Envoi en cours...",
    "auth.resend_success": "E-mail de vérification envoyé ! Vérifiez votre boîte mail et vos spams.",
    "auth.zero_knowledge": "Chiffrement de bout en bout. Architecture Zero-Knowledge.",
    "auth.register_subtitle": "Créez votre accès sécurisé avec votre email professionnel",
    "auth.connecting": "Connexion...",
    "auth.pwd_strong_required": "Le mot de passe doit contenir au moins 12 caractères, 1 majuscule, 1 minuscule, 1 chiffre et 1 symbole.",
    "auth.pwd_mismatch_error": "Les mots de passe ne correspondent pas.",
    "dash.title": "Tableau de Bord",
    "dash.upload": "NOUVEL AUDIT",
    "dash.history": "Historique des Audits",
    "dash.no_audits": "Aucun audit. Déposez votre premier fichier Excel pour commencer.",
    "dash.score": "Score de Santé",
    "dash.issues": "Problèmes",
    "dash.critical": "Critiques",
    "dash.date": "Date",
    "dash.actions": "Actions",
    "dash.plan": "Plan Actuel",
    "dash.free": "Gratuit",
    "dash.auditor": "Auditeur",
    "dash.firm": "Cabinet",
    "dash.upgrade": "S'ABONNER",
    "dash.expires": "Données expirent dans",
    "dash.hours": "heures",
    "dash.view": "Voir",
    "dash.download": "PDF",
    "dash.delete": "Supprimer",
    "dash.stats": "Vue d'ensemble",
    "dash.total_audits": "Total Audits",
    "dash.avg_score": "Score Moyen",
    "dash.total_issues": "Total Problèmes",
    "dash.usage": "Utilisation",
    "dash.audits_used": "Audits utilisés : {count} / 10",
    "dash.audits_unlimited": "Audits : Illimités",
    "dash.credits_reset": "Les crédits se rechargent le {date}",
    "dash.manage_billing": "Gérer mon abonnement",
    "upload.title": "Déposer un Fichier",
    "upload.drag": "Glissez-déposez votre fichier .xlsx ici",
    "upload.or": "ou cliquez pour parcourir",
    "upload.hint": "Fichiers .xlsx uniquement — Max 50 Mo",
    "upload.analyzing": "Analyse de votre tableur en cours...",
    "upload.step1": "Envoi du fichier",
    "upload.step2": "Analyse déterministe",
    "upload.step3": "Génération du rapport IA",
    "upload.step4": "Création du PDF",
    "upload.cta": "DÉMARRER L'AUDIT",
    "upload.drop_reject": "Seuls les fichiers .xlsx de moins de 50 Mo sont acceptés.",
    "upload.failed": "Échec du téléchargement. Veuillez réessayer.",
    "report.title": "Rapport d'Audit",
    "report.score": "Score de Santé",
    "report.risk": "Niveau de Risque",
    "report.summary": "Résumé Exécutif",
    "report.alerts": "Alertes Critiques",
    "report.findings": "Résultats Détaillés",
    "report.recommendations": "Recommandations",
    "report.download_pdf": "TÉLÉCHARGER LE PDF",
    "report.download_csv": "CSV",
    "report.unlock_csv": "Débloquer CSV Complet (Plan Cabinet)",
    "report.csv_limit_notice": "Le plan Auditeur est limité aux 50 premières erreurs. Passez au plan Cabinet pour débloquer l'export CSV exhaustif.",
    "report.kill_switch": "Détruire les Données (Kill-Switch)",
    "report.kill_switch_locked": "Kill-Switch (Plan Cabinet)",
    "report.kill_switch_confirm": "Êtes-vous sûr ? Cette action est irréversible. Toutes les données de l'audit seront définitivement détruites.",
    "report.back": "Retour au Tableau de Bord",
    "report.high": "ÉLEVÉ",
    "report.medium": "MOYEN",
    "report.low": "FAIBLE",
    "report.file": "Fichier",
    "report.issues": "Problèmes",
    "report.sheet": "Feuille",
    "report.cell": "Cellule",
    "report.auditor_upsell_prefix": "Seules les 50 premières erreurs sont affichées. ",
    "report.auditor_upsell_link": "Passez au plan Cabinet",
    "report.auditor_upsell_suffix": " pour débloquer le détail des {count} erreurs.",
    "report.not_found": "Rapport introuvable.",
    "report.sheets": "feuilles",
    "report.cells": "cellules",
    "report.critical_count": "critiques",
    "report.warnings_count": "avertissements",
    "report.rec": "Rec",
    "report.free_limit_notice": "Le plan Gratuit est limité aux 5 premières erreurs. Passez au plan payant pour débloquer le détail des autres cellules.",
    "report.auditor_limit_notice": "Le plan Auditeur est limité aux 50 premières erreurs. Passez au plan Cabinet pour débloquer l'export CSV exhaustif.",
    "report.issue_type_hardcoded_value": "Valeur Codée en Dur",
    "report.issue_type_formula_inconsistency": "Incohérence de Formule",
    "report.issue_type_circular_reference": "Référence Circulaire",
    "report.issue_type_external_reference": "Référence Externe",
    "report.issue_type_hidden_sheet": "Feuille Cachée",
    "report.issue_type_hidden_row": "Ligne Cachée",
    "report.issue_type_hidden_column": "Colonne Cachée",
    "report.issue_type_protected_sheet": "Feuille Protégée",
    "report.priority_high": "HAUTE",
    "report.priority_medium": "MOYENNE",
    "report.priority_low": "FAIBLE",
    "report.severity_critical": "CRITIQUE",
    "report.severity_warning": "AVERTISSEMENT",
    "report.severity_info": "INFO",
    "teaser.more_errors": "{count} erreur(s) supplémentaire(s) masquée(s). Passez au plan Auditeur pour débloquer le rapport complet.",
    "teaser.ghost_1": "Constante codée en dur dans une formule financière — remplacement manuel détecté",
    "teaser.ghost_2": "Incohérence de formule sur la plage de lignes — chaîne de références rompue",
    "teaser.ghost_3": "Dépendance circulaire dans la chaîne de calcul — boucle de recalcul",
    "teaser.ghost_4": "Nombre magique intégré au modèle dynamique — aucune plage nommée définie",
    "teaser.ghost_5": "Modèle de formule incohérent détecté dans la plage de colonnes",
    "teaser.ghost_6": "Lien vers classeur externe — risque de dépendance si le fichier source est déplacé",
    "teaser.ghost_7": "Ligne cachée contenant une formule active — lacune de visibilité de l'audit",
    "teaser.ghost_8": "Incompatibilité de formule matricielle — plage de cellules désynchronisée",
    "teaser.ghost_9": "Boucle d'auto-référence provoquant une erreur de recalcul silencieuse",
    "rate.limit_prefix": "Limite d'essai atteinte (2 audits max).",
    "rate.limit_link": "Abonnez-vous",
    "rate.limit_suffix": "pour continuer.",
    "settings.title": "Paramètres",
    "settings.account": "Compte",
    "settings.language": "Langue",
    "settings.subscription": "Abonnement",
    "settings.logout": "DÉCONNEXION",
    "settings.en": "Anglais",
    "settings.fr": "Français",
    "settings.name": "Nom",
    "settings.email": "Email",
    "settings.audits_used": "Audits Utilisés",
    "settings.next_billing": "Prochaine facturation",
  }
};

const LanguageContext = createContext();

export function LanguageProvider({ children }) {
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('sa_lang') || 'en';
  });

  const changeLanguage = useCallback((lang) => {
    setLanguage(lang);
    localStorage.setItem('sa_lang', lang);
  }, []);

  const t = useCallback((key) => {
    return translations[language]?.[key] || translations['en']?.[key] || key;
  }, [language]);

  return (
    <LanguageContext.Provider value={{ language, changeLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useTranslation() {
  const context = useContext(LanguageContext);
  if (!context) throw new Error('useTranslation must be used within LanguageProvider');
  return context;
}
